import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Field } from "../components/common";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { errMsg } from "../lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const { t, lang, setLang } = useT();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-black lg:block">
        <img src="/brand/login-hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="relative flex h-full flex-col justify-end p-14 text-white">
          <div className="mb-6 h-1 w-16 bg-[#db0720]" />
          <h2 className="max-w-lg text-4xl font-light leading-tight"><b className="font-bold">Connected</b> to power your <b className="font-bold">performance</b></h2>
          <p className="mt-4 max-w-md text-sm text-neutral-300">{t("login.tagline")}</p>
        </div>
      </div>
      <div className="flex flex-col bg-white">
        <div className="flex justify-end gap-1 p-6">
          {["es", "en"].map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`login-lang-${l}`}
              className={`rounded-full px-3 py-1 text-xs font-bold ${lang === l ? "bg-black text-white" : "text-neutral-500 hover:bg-neutral-100"}`}>{l.toUpperCase()}</button>
          ))}
        </div>
        <div className="flex flex-1 items-center justify-center px-6 pb-16">
          <form onSubmit={submit} className="page-enter w-full max-w-sm space-y-6" data-testid="login-form">
            <img src="/brand/logo-bobst.svg" alt="BOBST" className="h-9" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{t("login.title")}</h1>
              <p className="mt-1 text-sm text-neutral-500">{t("login.subtitle")}</p>
            </div>
            <Field label={t("login.email")} required>
              <Input data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
            </Field>
            <Field label={t("login.password")} required>
              <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
            </Field>
            {error && <div data-testid="login-error" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            <Button type="submit" disabled={busy} data-testid="login-submit-btn" className="h-11 w-full bg-[#db0720] text-white hover:bg-[#a90707]">
              {t("login.submit")} <ArrowRight className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
